// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as array from './array';
import { MaybeArray, NonEmptyArray } from './array';
import { MaybeString, NullableString, isNonEmptyString, isString as isStringFunc } from './string';

/**
 * Throws exception with specified message.
 * @example assert.fail('This is a failure message.');
 */
export function fail(message: string): never {
    throw new Error(message);
}

/**
 * Runs validation function and throws an error if it returns an error.
 * Check also check the result of a validation function if passed as a parameter.
 * @example assert.isValid(validation.required(param, 'param'));
 */
export function isValid(validation: NullableString | (() => NullableString)): void | never {
    const result = typeof validation === 'function' ? validation() : validation;
    if (isStringFunc(result)) {
        throw Error(result);
    }
}

/**
 * Throws an error if given value is not a string.
 * @example assert.isString(param, 'Parameter must not be a string.');
 */
export function isString(value: MaybeString, message?: string): asserts value is string {
    if (!isStringFunc(value)) {
        throw new Error(message ?? 'Value must be a string.');
    }
}

/**
 * Throws an error if given value is not a non-empty string.
 * @example assert.isNotEmpty(param, 'Parameter must not be empty.');
 */
export function isNotEmpty(value: MaybeString, message?: string): asserts value is string {
    if (!isNonEmptyString(value)) {
        throw new Error(message ?? 'Value must be a non-empty string.');
    }
}

/**
 * Throws an error if given value is not null, empty or undefined string.
 * @example assert.isEmpty(value, 'Parameter must not be defined.';
 */
export function isEmpty(value: MaybeString, message?: string): asserts value is undefined | null | '' {
    if (value !== undefined && value !== null && value !== '') {
        throw new Error(message ?? 'Value must be an empty string, null or undefined.');
    }
}

/**
 * Throws an error if given condition is true.
 * @example assert.isTrue(param !== null, 'Parameter must not be null.');
 */
export function isTrue(condition: boolean, message?: string): asserts condition is true {
    if (!condition) {
        throw new Error(message ?? `Assert violation.`);
    }
}

/**
 * Throws an error if given condition is false.
 * @example assert.isFalse(param === null, 'Parameter must not be null.');
 */
export function isFalse(condition: boolean, message?: string): asserts condition is false {
    if (condition) {
        throw new Error(message ?? `Assert violation.`);
    }
}

/**
 * Throws an error if given value is not a valid array index (non-negative integer).
 * @example assert.isValidIndex(index, 'Parameter must be a valid index.');
 */
export function isValidIndex(value: unknown, message?: string): asserts value is number {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(message ?? `Value must be an integer, greater or equal to zero.`);
    }
}

/**
 * Throws an error if given value is not a non-empty array.
 * @example assert.isNonEmptyArray(param, 'Parameter must not be an empty array.');
 */
export function isNonEmptyArray<T>(value: MaybeArray<T>, message?: string): asserts value is NonEmptyArray<T> {
    if (!array.isNonEmptyArray(value)) {
        throw new Error(message ?? 'Value must be a non-empty array.');
    }
}

/**
 * Throws an error if given value is null or undefined.
 * @example assert.isDefined(param, 'Parameter must be defined.');
 */
export function isDefined<T>(value: T, message?: string): asserts value is NonNullable<T> {
    if (value === null || value === undefined) {
        throw new Error(message ?? 'Value must be defined.');
    }
}

/**
 * Throws an error when unreachable code (e.g. in a switch statement) is reached.
 * @example switch (param) { ... default: assert.unreachable(param, 'param); }
 */
export function unreachable(value?: NullableString, name?: string): never {
    const hasValue = isStringFunc(value);
    const hasName = isStringFunc(name);
    if (hasValue && hasName) {
        throw new Error(`Value '${value}' of '${name}' was not expected.`);
    } else if (hasValue) {
        throw new Error(`Value '${value}' was not expected.`);
    } else if (hasName) {
        throw new Error(`Value of '${name}' was not expected.`);
    } else {
        throw new Error('Unexpected code path was reached.');
    }
}
